import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  cancelOrder,
  completeOrder,
  createCalibrationRecord,
  createEquipment,
  createOrder,
  createPlan,
  createRecord,
  deactivateEquipment,
  fetchAlertsPreview,
  fetchCalibrationRecords,
  fetchEquipment,
  fetchEquipmentList,
  fetchOrders,
  fetchPlans,
  fetchPreventiveVsCorrective,
  fetchRecords,
  updateEquipment,
  updatePlan,
} from './maintenanceApi';
import type {
  CalibrationRecord,
  CalibrationRecordPayload,
  Equipment,
  EquipmentListParams,
  EquipmentListResponse,
  EquipmentPayload,
  EquipmentStatus,
  MaintenanceAlertPreview,
  MaintenanceOrder,
  MaintenanceOrderPayload,
  MaintenancePlan,
  MaintenancePlanPayload,
  MaintenanceRecord,
  MaintenanceRecordPayload,
  MaintenanceReportParams,
  Pagination,
  PreventiveVsCorrectiveReport,
} from './types';

interface MaintenanceState {
  equipment: Equipment[];
  equipmentLoading: boolean;
  pagination: Pagination;
  search: string;
  filterStatus: EquipmentStatus | '';
  filterInstrument: boolean | null;
  current: Equipment | null;
  plans: MaintenancePlan[];
  orders: MaintenanceOrder[];
  records: MaintenanceRecord[];
  calibrations: CalibrationRecord[];
  alertsPreview: MaintenanceAlertPreview | null;
  report: PreventiveVsCorrectiveReport | null;
  reportLoading: boolean;
}

const initialState: MaintenanceState = {
  equipment: [],
  equipmentLoading: false,
  pagination: { page: 1, pageSize: 20, total: 0, pages: 0 },
  search: '',
  filterStatus: '',
  filterInstrument: null,
  current: null,
  plans: [],
  orders: [],
  records: [],
  calibrations: [],
  alertsPreview: null,
  report: null,
  reportLoading: false,
};

export const loadEquipment = createAsyncThunk<EquipmentListResponse, EquipmentListParams | undefined>(
  'maintenance/loadEquipment',
  async (params) => fetchEquipmentList(params ?? {}),
);

export const loadOneEquipment = createAsyncThunk<Equipment, string>(
  'maintenance/loadOne',
  async (id) => fetchEquipment(id),
);

export const createEquipmentThunk = createAsyncThunk<Equipment, EquipmentPayload>(
  'maintenance/createEq',
  async (payload) => createEquipment(payload),
);

export const updateEquipmentThunk = createAsyncThunk<Equipment, { id: string; payload: EquipmentPayload }>(
  'maintenance/updateEq',
  async ({ id, payload }) => updateEquipment(id, payload),
);

export const deactivateEquipmentThunk = createAsyncThunk<Equipment, string>(
  'maintenance/deactivateEq',
  async (id) => deactivateEquipment(id),
);

export const loadPlans = createAsyncThunk<MaintenancePlan[], string>(
  'maintenance/loadPlans',
  async (eqId) => fetchPlans(eqId),
);

export const createPlanThunk = createAsyncThunk<
  MaintenancePlan,
  { equipmentId: string; payload: MaintenancePlanPayload }
>('maintenance/createPlan', async ({ equipmentId, payload }) => createPlan(equipmentId, payload));

export const updatePlanThunk = createAsyncThunk<
  MaintenancePlan,
  { equipmentId: string; planId: string; payload: MaintenancePlanPayload }
>('maintenance/updatePlan', async ({ equipmentId, planId, payload }) => updatePlan(equipmentId, planId, payload));

export const loadAlertsPreview = createAsyncThunk<MaintenanceAlertPreview>(
  'maintenance/loadAlertsPreview',
  async () => fetchAlertsPreview(),
);

export const loadMaintenanceReport = createAsyncThunk<
  PreventiveVsCorrectiveReport,
  MaintenanceReportParams | undefined
>('maintenance/loadReport', async (params) => fetchPreventiveVsCorrective(params ?? {}));

export const loadOrders = createAsyncThunk<MaintenanceOrder[], string | undefined>(
  'maintenance/loadOrders',
  async (equipmentId) => {
    const r = await fetchOrders({ equipmentId });
    return r.data;
  },
);

export const createOrderThunk = createAsyncThunk<MaintenanceOrder, MaintenanceOrderPayload>(
  'maintenance/createOrder',
  async (payload) => createOrder(payload),
);

export const completeOrderThunk = createAsyncThunk<MaintenanceOrder, { id: string; resultNotes?: string | null; cost?: number | null }>(
  'maintenance/completeOrder',
  async ({ id, resultNotes, cost }) => completeOrder(id, { resultNotes, cost }),
);

export const cancelOrderThunk = createAsyncThunk<MaintenanceOrder, string>(
  'maintenance/cancelOrder',
  async (id) => cancelOrder(id),
);

export const loadRecords = createAsyncThunk<MaintenanceRecord[], string | undefined>(
  'maintenance/loadRecords',
  async (orderId) => fetchRecords(orderId),
);

export const createRecordThunk = createAsyncThunk<MaintenanceRecord, MaintenanceRecordPayload>(
  'maintenance/createRecord',
  async (payload) => createRecord(payload),
);

export const loadCalibrations = createAsyncThunk<CalibrationRecord[], string>(
  'maintenance/loadCalibrations',
  async (equipmentId) => fetchCalibrationRecords(equipmentId),
);

export const createCalibrationThunk = createAsyncThunk<
  CalibrationRecord,
  { equipmentId: string; payload: CalibrationRecordPayload }
>('maintenance/createCalibration', async ({ equipmentId, payload }) =>
  createCalibrationRecord(equipmentId, payload),
);

const slice = createSlice({
  name: 'maintenance',
  initialState,
  reducers: {
    setSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    },
    setFilterStatus(state, action: PayloadAction<EquipmentStatus | ''>) {
      state.filterStatus = action.payload;
    },
    setFilterInstrument(state, action: PayloadAction<boolean | null>) {
      state.filterInstrument = action.payload;
    },
    setPage(state, action: PayloadAction<number>) {
      state.pagination.page = action.payload;
    },
    clearCurrent(state) {
      state.current = null;
      state.plans = [];
      state.orders = [];
      state.records = [];
      state.calibrations = [];
      state.alertsPreview = null;
    },
    clearReport(state) {
      state.report = null;
      state.reportLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadEquipment.pending, (state) => {
        state.equipmentLoading = true;
      })
      .addCase(loadEquipment.fulfilled, (state, action) => {
        state.equipmentLoading = false;
        state.equipment = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(loadEquipment.rejected, (state) => {
        state.equipmentLoading = false;
      })
      .addCase(loadOneEquipment.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      .addCase(createEquipmentThunk.fulfilled, (state, action) => {
        state.equipment = [action.payload, ...state.equipment];
      })
      .addCase(updateEquipmentThunk.fulfilled, (state, action) => {
        state.equipment = state.equipment.map((e) => (e.id === action.payload.id ? action.payload : e));
        if (state.current?.id === action.payload.id) state.current = { ...state.current, ...action.payload };
      })
      .addCase(deactivateEquipmentThunk.fulfilled, (state, action) => {
        state.equipment = state.equipment.map((e) => (e.id === action.payload.id ? action.payload : e));
      })
      .addCase(loadPlans.fulfilled, (state, action) => {
        state.plans = action.payload;
      })
      .addCase(createPlanThunk.fulfilled, (state, action) => {
        state.plans = [action.payload, ...state.plans];
      })
      .addCase(updatePlanThunk.fulfilled, (state, action) => {
        state.plans = state.plans.map((p) => (p.id === action.payload.id ? action.payload : p));
      })
      .addCase(loadAlertsPreview.fulfilled, (state, action) => {
        state.alertsPreview = action.payload;
      })
      .addCase(loadMaintenanceReport.pending, (state) => {
        state.reportLoading = true;
      })
      .addCase(loadMaintenanceReport.fulfilled, (state, action) => {
        state.reportLoading = false;
        state.report = action.payload;
      })
      .addCase(loadMaintenanceReport.rejected, (state) => {
        state.reportLoading = false;
      })
      .addCase(loadOrders.fulfilled, (state, action) => {
        state.orders = action.payload;
      })
      .addCase(createOrderThunk.fulfilled, (state, action) => {
        state.orders = [action.payload, ...state.orders];
      })
      .addCase(completeOrderThunk.fulfilled, (state, action) => {
        state.orders = state.orders.map((o) => (o.id === action.payload.id ? action.payload : o));
      })
      .addCase(cancelOrderThunk.fulfilled, (state, action) => {
        state.orders = state.orders.map((o) => (o.id === action.payload.id ? action.payload : o));
      })
      .addCase(loadRecords.fulfilled, (state, action) => {
        state.records = action.payload;
      })
      .addCase(createRecordThunk.fulfilled, (state, action) => {
        state.records = [action.payload, ...state.records];
      })
      .addCase(loadCalibrations.fulfilled, (state, action) => {
        state.calibrations = action.payload;
      })
      .addCase(createCalibrationThunk.fulfilled, (state, action) => {
        state.calibrations = [action.payload, ...state.calibrations];
      });
  },
});

export const { setSearch, setFilterStatus, setFilterInstrument, setPage, clearCurrent, clearReport } =
  slice.actions;
export default slice.reducer;
